import { CloseOutlineIcon, OpenInNewOutlineIcon } from '@applyboard/ui-icons';
import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import React, { Fragment } from 'react';
import { Box } from '../Box';
import { Button } from '../Button';
import { Flex } from '../Flex';
import { Text } from '../Text';
import { Tooltip, TooltipProps } from '../Tooltip';
import { IconType, useFullTheme } from '~/design';
import { IconWrapper, useLinkAs } from '~/internal';
import { assert, useId, useResponsive } from '~/utils';
import { allowedTooltipSymbols } from '~/utils/tooltip';
import { Divider } from '../Divider';
import { SrOnly } from '../SrOnly';

type ChildType = React.ReactChild | React.ReactFragment | React.ReactPortal;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ComparisonArgumentType = string | React.JSXElementConstructor<any>;

const isChildType = (child: ChildType, type: ComparisonArgumentType) =>
  child instanceof Object && 'type' in child && child.type === type;

const findChildOfType = (children: ChildType, type: ComparisonArgumentType) =>
  React.Children.toArray(children).find(child => isChildType(child, type));
const findChildrenOfType = (children: ChildType, types: ComparisonArgumentType[]) =>
  React.Children.toArray(children).filter(child => types.some(type => isChildType(child, type)));

const MenuButtonContext = React.createContext(false);

export interface MenuButtonProps {
  /**
   * The alignment of the menu relative to its anchor. The value of this prop should be equivalent
   * to where the menu's trigger is located. For example, if the button to open the menu is on the
   * far-right of the screen, `alignment=right` should be used.
   */
  alignment?: 'left' | 'center' | 'right';

  /** The trigger of the menu + content of the menu. */
  children: ChildType;

  /** How the menu will grow horizontally. `md` will show the menu in 320px wide, while `hug`
   * will respect its contents' width.
   **/
  menuWidth?: 'md' | 'hug';

  /** A callback function that is invoked when the menu is opened or closed. */
  onOpenChange: (value: boolean) => void;

  /** Represents whether or not the menu is open. */
  open: boolean;
}

/**
 * MenuButton is a button that shows a popover menu when clicked.
 */
export function MenuButton({
  alignment = 'left',
  children,
  menuWidth = 'md',
  onOpenChange,
  open,
}: MenuButtonProps) {
  const theme = useFullTheme();

  const triggerChild = findChildOfType(children, MenuButton.Trigger);
  const headerChild = findChildOfType(children, MenuButton.Header);
  const itemChildren = findChildrenOfType(children, [
    MenuButton.Link,
    MenuButton.Button,
    MenuButton.Divider,
    MenuButton.Label,
    MenuButton.Tooltip,
  ]);
  const footerChild = findChildOfType(children, MenuButton.Footer);
  const childId = useId();

  const alignmentProp = (
    {
      left: 'start',
      center: 'center',
      right: 'end',
    } as const
  )[alignment];

  return (
    <MenuButtonContext.Provider value={true}>
      <RadixDropdownMenu.Root onOpenChange={onOpenChange} open={open}>
        {triggerChild}

        <RadixDropdownMenu.Portal>
          <RadixDropdownMenu.Content asChild align={alignmentProp} sideOffset={8}>
            <div
              css={{
                backgroundColor: theme.colors.bgRegular,
                borderRadius: theme.borderRadiuses.container,
                boxShadow: theme.elevations.z1,
                paddingInline: theme.spacing[2],
                width: menuWidth === 'md' ? '320px' : 'auto',
                zIndex: theme.zIndex.menu,
              }}
            >
              {headerChild}

              <Flex direction="column">
                {itemChildren.map((child, index) => {
                  // @ts-expect-error Something weird with the child variable
                  const itemId = child.props.id || `${childId}-${index}`;
                  const isFirst = index === 0;
                  const isLast = index === itemChildren.length - 1;
                  const isDivider = isChildType(child, MenuButton.Divider);
                  const isLabel = isChildType(child, MenuButton.Label);
                  const isTooltip = isChildType(child, MenuButton.Tooltip);

                  if (isDivider) {
                    return (
                      <div
                        css={{
                          marginInline: `-${theme.spacing[2]}`,
                        }}
                        key={`__divider-${index}`}
                      >
                        <Divider m={0} />
                      </div>
                    );
                  }

                  if (isLabel) {
                    return <Fragment key={`__label-${index}`}>{child}</Fragment>;
                  }

                  let rounded: MenuButtonItemWrapperProps['rounded'] = 'none';
                  if (isFirst && isLast) {
                    if (!headerChild && !footerChild) {
                      rounded = 'all';
                    } else if (!headerChild) {
                      rounded = 'top';
                    } else if (!footerChild) {
                      rounded = 'bottom';
                    }
                  } else if (isFirst) {
                    rounded = headerChild ? 'none' : 'top';
                  } else if (isLast) {
                    rounded = footerChild ? 'none' : 'bottom';
                  }

                  if (isTooltip) {
                    // @ts-expect-error IDK how to fix this one
                    const { children: tooltipChild, ...tooltipProps } = child.props;
                    assert(
                      isChildType(tooltipChild, MenuButton.Button) ||
                        isChildType(tooltipChild, MenuButton.Link),
                      'MenuButton.Tooltip can only be used with MenuButton.Button or MenuButton.Link',
                    );

                    return (
                      <MenuButtonItemWrapper
                        element={tooltipChild}
                        key={itemId}
                        rounded={rounded}
                        parentProps={{ tooltip: tooltipProps }}
                      />
                    );
                  }

                  return (
                    <MenuButtonItemWrapper
                      // @ts-expect-error IDK how to fix this one
                      element={child}
                      key={itemId}
                      rounded={rounded}
                    />
                  );
                })}
              </Flex>

              {footerChild}
            </div>
          </RadixDropdownMenu.Content>
        </RadixDropdownMenu.Portal>
      </RadixDropdownMenu.Root>
    </MenuButtonContext.Provider>
  );
}

interface ItemProps {
  /** The element's text. */
  children: React.ReactNode;

  /** Represents whether or not the element can be clicked. */
  disabled?: boolean;

  /** The icon to the left of the element's text. */
  icon?: IconType;

  /** A unique identifier for the element. This will _not_ set the HTML "id" attribute. */
  id?: string;

  /** A callback function that is invoked when the element is clicked. */
  onClick: () => void;
}

export type MenuButtonButtonProps = ItemProps;

MenuButton.Button = function MenuButtonButton(props: MenuButtonButtonProps) {
  if (!React.useContext(MenuButtonContext)) {
    throw new Error('`MenuButton.Button` can only be rendered inside `MenuButton`');
  }

  return <>{props.children}</>;
};

export interface MenuButtonLinkProps {
  /** The link text. */
  children: string;

  /** Represents whether or not the link can be clicked. */
  disabled?: boolean;

  /** The link's href. */
  href: string;

  /** The filename for download links. */
  download?: string;

  /** The icon to the left of the link text. */
  icon?: IconType;

  /** A unique identifier for the link. This will _not_ set the HTML "id" attribute. */
  id?: string;

  /** Whether or not the link opens in a new tab. */
  mode?: 'override_user_choice_and_open_in_new_tab';

  /** A callback function that is invoked when the button is clicked. Useful for event tracking. */
  onClick?: () => void;
}

MenuButton.Link = function MenuButtonLink(props: MenuButtonLinkProps) {
  if (!React.useContext(MenuButtonContext)) {
    throw new Error('`MenuButton.Link` can only be rendered inside `MenuButton`');
  }

  return <>{props.children}</>;
};

export interface MenuButtonHeaderProps {
  /** The child of the heading. */
  children: React.ReactNode;

  /** A callback function that is invoked when the Close icon is clicked. */
  onClickClose?: () => void;
}

MenuButton.Header = function MenuButtonHeader({ children, onClickClose }: MenuButtonHeaderProps) {
  if (!React.useContext(MenuButtonContext)) {
    throw new Error('`MenuButton.Header` can only be rendered inside `MenuButton`');
  }

  return (
    <Flex align="center" gap={2} justify="between" pb={2} pt={3}>
      <Box pl={2}>{children}</Box>

      {onClickClose && (
        <Button
          aria-label="Close"
          emphasis="transparent"
          leadIcon={CloseOutlineIcon}
          onClick={onClickClose}
        />
      )}
    </Flex>
  );
};

export interface MenuButtonFooterProps {
  /** The children of the footer. */
  children: React.ReactNode;
}

MenuButton.Footer = function MenuButtonFooter({ children }: MenuButtonFooterProps) {
  if (!React.useContext(MenuButtonContext)) {
    throw new Error('`MenuButton.Footer` can only be rendered inside `MenuButton`');
  }

  return <Box py={2}>{children}</Box>;
};

export interface MenuButtonTriggerProps {
  children: React.ReactNode;
}

MenuButton.Trigger = function MenuButtonTrigger({ children }: MenuButtonTriggerProps) {
  if (!React.useContext(MenuButtonContext)) {
    throw new Error('`MenuButton.Trigger` can only be rendered inside `MenuButton`');
  }

  return <RadixDropdownMenu.Trigger asChild>{children}</RadixDropdownMenu.Trigger>;
};

MenuButton.Divider = function MenuButtonDivider() {
  if (!React.useContext(MenuButtonContext)) {
    throw new Error('`MenuButton.Divider` can only be rendered inside `MenuButton`');
  }

  return <></>;
};

export interface MenuButtonLabelProps {
  /** The children of the label. */
  children: string;
}

MenuButton.Label = function MenuButtonLabel({ children }: MenuButtonLabelProps) {
  if (!React.useContext(MenuButtonContext)) {
    throw new Error('`MenuButton.Label` can only be rendered inside `MenuButton`');
  }

  return (
    <Box pt={3} pb={2} px={2}>
      <Text variant="labelM">{children}</Text>
    </Box>
  );
};

export interface MenuButtonTooltipProps extends TooltipProps {}
MenuButton.Tooltip = function MenuButtonTooltip({ children }: MenuButtonTooltipProps) {
  if (!React.useContext(MenuButtonContext)) {
    throw new Error('`MenuButton.Tooltip` can only be rendered inside `MenuButton`');
  }
  return <>{children}</>;
};

MenuButton.Item = RadixDropdownMenu.Item;
Object.assign(MenuButton.Item, { [allowedTooltipSymbols.MenuButtonItem]: true });

interface MenuButtonItemWrapperProps {
  element: React.ReactElement<MenuButtonButtonProps | MenuButtonLinkProps>;
  parentProps?: {
    tooltip?: TooltipProps;
  };
  rounded: 'all' | 'top' | 'bottom' | 'none';
}

function MenuButtonItemWrapper({ element, parentProps, rounded }: MenuButtonItemWrapperProps) {
  const theme = useFullTheme();
  const responsive = useResponsive();
  const LinkAs = useLinkAs();

  const {
    children,
    disabled,
    // @ts-expect-error "download" is on MenuButton.Link, not on MenuButton.Button
    download,
    // @ts-expect-error "href" is on MenuButton.Link, not on MenuButton.Button
    href,
    icon: Icon,
    // @ts-expect-error "mode" is on MenuButton.Link, not on MenuButton.Button
    mode,
    onClick,
  } = element.props;

  if (download)
    assert(
      element.type === MenuButton.Link,
      'The "download" prop is only available for MenuButton.Link.',
    );

  if (href)
    assert(
      element.type === MenuButton.Link,
      'The "href" prop is only available for MenuButton.Link.',
    );

  const isButton = element.type === MenuButton.Button;
  const Tag = isButton ? 'button' : disabled ? 'span' : LinkAs;
  const isNewTabLink = !isButton && mode === 'override_user_choice_and_open_in_new_tab';

  const menuItem = (
    <MenuButton.Item asChild disabled={disabled}>
      <Tag
        css={[
          {
            backgroundColor: 'transparent',
            border: 0,
            cursor: 'pointer',
            marginInline: `-${theme.spacing[2]}`,
            padding: `${theme.spacing[3]} ${theme.spacing[4]}`,
            '&:hover': {
              backgroundColor: theme.colors.bgRegularHover,
            },
            '&:active': {
              backgroundColor: theme.colors.bgRegularPressed,
            },
          },
          ['top', 'all'].includes(rounded) && {
            borderTopLeftRadius: theme.borderRadiuses.container,
            borderTopRightRadius: theme.borderRadiuses.container,
          },
          ['bottom', 'all'].includes(rounded) && {
            borderBottomLeftRadius: theme.borderRadiuses.container,
            borderBottomRightRadius: theme.borderRadiuses.container,
          },
          !disabled && {
            '&:focus': {
              // FIXME: for some reason this gets triggered on hover. I suspect it's to do with
              // bugs in the radix-ui/react-dropdown-menu package.
              boxShadow: `inset ${theme.colors.actionPrimary} 0px 0px 0px 1px, ${theme.colors.actionPrimary} 0px 0px 0px 1px`,
              outline: 'none',
            },
          },
          disabled && {
            backgroundColor: 'transparent',
            cursor: 'not-allowed',
            '&:hover': {
              backgroundColor: 'transparent',
            },
            '&:active': {
              backgroundColor: 'transparent',
            },
          },
        ]}
        disabled={disabled}
        download={download}
        href={disabled ? undefined : href}
        onClick={disabled ? undefined : onClick}
        role="menuitem"
        target={isNewTabLink ? '_blank' : undefined}
        rel={isNewTabLink ? 'noopener' : undefined}
      >
        <Flex gap={4}>
          {!!Icon && (
            <Flex.Item shrink={0}>
              <IconWrapper
                contrast="high"
                disabled={disabled}
                intent="secondary"
                selector={isButton ? 'button' : 'a'}
              >
                <Icon />
              </IconWrapper>
            </Flex.Item>
          )}
          <Flex align="start" gap={2}>
            <span
              css={[
                responsive.css(theme.typography.bodyS),
                !disabled && {
                  color: theme.colors.textSecondaryStark,
                  '&:hover': {
                    color: theme.colors.textSecondaryStarkHover,
                  },
                  '&:active': {
                    color: theme.colors.textSecondaryStarkPressed,
                  },
                },
                disabled && {
                  color: theme.colors.textInactive,
                },
              ]}
            >
              {children}
              {isNewTabLink && <SrOnly>&nbsp;(Opens in new tab)</SrOnly>}
            </span>
            {isNewTabLink && (
              <Flex.Item shrink={0} grow={0} basis="24px">
                <OpenInNewOutlineIcon size="md" />
              </Flex.Item>
            )}
          </Flex>
        </Flex>
      </Tag>
    </MenuButton.Item>
  );
  if (parentProps?.tooltip && !disabled) {
    return <Tooltip {...parentProps.tooltip}>{menuItem}</Tooltip>;
  }

  return menuItem;
}
